/**
 * Selection utility functions for managing multi-select states
 */

/**
 * Toggle selection for a single item
 * @param {Object} selectionMap - Current selection map
 * @param {string|number} itemId - Item ID to toggle
 * @returns {Object} Updated selection map
 */
export const toggleItemSelection = (selectionMap, itemId) => {
  const next = { ...selectionMap }
  if (next[itemId]) {
    delete next[itemId]
  } else {
    next[itemId] = true
  }
  return next
}

/**
 * Select all items
 * @param {Array} items - Array of items to select
 * @param {string} idKey - Key to use as ID (default: 'id')
 * @returns {Object} Selection map with all items selected
 */
export const selectAllItems = (items, idKey = 'id') => {
  const map = {}
  items.forEach(item => {
    map[item[idKey]] = true
  })
  return map
}

/**
 * Deselect all items
 * @returns {Object} Empty selection map
 */
export const deselectAllItems = () => {
  return {}
}

/**
 * Check if all items are selected
 * @param {Array} items - Array of items
 * @param {Object} selectionMap - Current selection map
 * @param {string} idKey - Key to use as ID (default: 'id')
 * @returns {boolean} True if all items are selected
 */
export const areAllItemsSelected = (items, selectionMap, idKey = 'id') => {
  if (items.length === 0) return false
  return items.every(item => selectionMap[item[idKey]])
}

/**
 * Get selected items from array
 * @param {Array} items - Array of items
 * @param {Object} selectionMap - Current selection map
 * @param {string} idKey - Key to use as ID (default: 'id')
 * @returns {Array} Array of selected items
 */
export const getSelectedItems = (items, selectionMap, idKey = 'id') => {
  return items.filter(item => selectionMap[item[idKey]])
}

/**
 * Get selected item IDs
 * @param {Object} selectionMap - Current selection map
 * @returns {Array} Array of selected IDs
 */
export const getSelectedIds = (selectionMap) => {
  return Object.keys(selectionMap).filter(id => selectionMap[id])
}

/**
 * Get selection count
 * @param {Object} selectionMap - Current selection map
 * @returns {number} Number of selected items
 */
export const getSelectionCount = (selectionMap) => {
  return Object.keys(selectionMap).filter(id => selectionMap[id]).length
}
